import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AccountBalanceWalletOutlined from "@mui/icons-material/AccountBalanceWalletOutlined";
import AddRounded from "@mui/icons-material/AddRounded";
import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import EditRounded from "@mui/icons-material/EditRounded";
import ErrorOutlineRounded from "@mui/icons-material/ErrorOutlineRounded";
import GroupsOutlined from "@mui/icons-material/GroupsOutlined";
import PaidOutlined from "@mui/icons-material/PaidOutlined";
import ReceiptLongOutlined from "@mui/icons-material/ReceiptLongOutlined";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import SaveRounded from "@mui/icons-material/SaveRounded";
import SpeedOutlined from "@mui/icons-material/SpeedOutlined";
import TimelapseRounded from "@mui/icons-material/TimelapseRounded";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import WhatsApp from "@mui/icons-material/WhatsApp";
import { Navigate, NavLink, useLocation } from "react-router-dom";
import OperationsTheme from "../components/shared/OperationsTheme";
import ConditionTypesConfigPanel from "../features/cobranca/components/ConditionTypeExclusionsPanel";
import CobrancaCargasPage from "../features/cobranca/pages/CobrancaCargasPage";
import CobrancaTitulosPage from "../features/cobranca/pages/CobrancaTitulosPage";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import cobrancaDashboardService from "../features/cobranca/services/cobrancaDashboard.service";
import cobrancaNotificacoesService from "../features/cobranca/services/cobrancaNotificacoes.service";
import {
  formatAverageDays,
  formatCompactCurrency,
  formatCurrency,
  formatDateTime,
  formatDays,
  formatNumber,
} from "../features/cobranca/utils/formatters";

const getCobrancaColors = () => ({
  background: "var(--ops-canvas)",
  border: "var(--ops-border)",
  card: "var(--ops-surface)",
  cardAlt: "var(--ops-subtle)",
  text: "var(--ops-text)",
  muted: "var(--ops-muted)",
  softMuted: "var(--ops-muted)",
  primary: "var(--ops-accent)",
  green: "var(--ops-success)",
  orange: "var(--ops-warning)",
  red: "var(--ops-danger)",
  purple: "var(--ops-chart-medium)",
  teal: "var(--ops-chart-teal)",
  slate: "var(--ops-heading)",
});

const getCobrancaChartPalette = (theme) => {
  const isDark = theme.palette.mode === "dark";

  return {
    primary: theme.palette.primary.main,
    green: isDark ? "#34d399" : "#059669",
    orange: isDark ? "#fb923c" : "#ea580c",
    red: isDark ? "#f87171" : "#dc2626",
    purple: isDark ? "#a78bfa" : "#7c3aed",
    teal: isDark ? "#22d3ee" : "#0891b2",
  };
};

const getChartColors = (colors) => [colors.chart.primary, colors.chart.orange, colors.chart.green, colors.chart.purple, colors.chart.teal, colors.chart.red];

const emptyDashboard = {
  ultimaCarga: null,
  cards: {},
  faixasAtraso: [],
  topClientes: [],
  centrosCusto: [],
  composicaoValor: {},
  spcSerasa: {},
  saudeCarga: {},
  evolucao: {
    diaria: [],
    mensal: [],
  },
};

const cobrancaNavItems = [
  { key: "visao-geral", label: "Visão Geral", to: "/cobranca/visao-geral" },
  { key: "titulos", label: "Títulos", to: "/cobranca/titulos" },
  { key: "avisos", label: "Avisos", to: "/cobranca/avisos" },
  { key: "whatsapp", label: "Whatsapp", to: "/cobranca/whatsapp" },
  { key: "configuracao", label: "Configuração", to: "/cobranca/configuracao" },
  { key: "cargas", label: "Cargas", to: "/cobranca/cargas" },
];

function CobrancaShell({ children }) {
  const location = useLocation();
  const activePage = cobrancaNavItems.find((item) => item.to === location.pathname);

  return (
    <OperationsTheme>
      <Box sx={{
        mx: { xs: -2, md: -3 },
        my: -3,
        px: { xs: 2, md: 3 },
        pt: { xs: 1.25, md: 1.6 },
        pb: 2,
        minHeight: "100%",
        color: "var(--ops-text)",
        background: "linear-gradient(180deg, var(--ops-canvas) 0%, var(--ops-canvas-end) 100%)",
      }}>
        <Paper elevation={0} sx={{ border: "1px solid var(--ops-border)", borderRadius: 2, overflow: "hidden", mb: 1.25, bgcolor: "var(--ops-nav)" }}>
          <Box
            component="nav"
            aria-label="Páginas de Inadimplência"
            sx={{ display: "flex", alignItems: "center", minWidth: 0, flexWrap: "wrap", px: { xs: 0.8, md: 1.1 }, py: 0.3 }}
          >
            {cobrancaNavItems.map((item) => {
              const active = activePage?.key === item.key;
              return (
                <Button
                  key={item.key}
                  component={NavLink}
                  to={item.to}
                  color="inherit"
                  aria-current={active ? "page" : undefined}
                  sx={{
                    position: "relative",
                    minWidth: { xs: 88, sm: 108 },
                    flexShrink: 0,
                    px: { xs: 0.7, md: 0.6, lg: 0.75 },
                    py: 0.65,
                    borderRadius: 0,
                    color: active ? "var(--ops-accent)" : "var(--ops-text)",
                    fontWeight: active ? 800 : 600,
                    fontSize: 12.5,
                    whiteSpace: "nowrap",
                    "&::after": active ? {
                      content: '""', position: "absolute", height: 3, left: 10, right: 10, bottom: 0,
                      borderRadius: "3px 3px 0 0", bgcolor: "var(--ops-accent)",
                    } : {},
                  }}
                >
                  {item.label}
                </Button>
              );
            })}
          </Box>
        </Paper>
        <Box sx={{
          minWidth: 0,
          "& .MuiButton-root": { fontSize: 11.5, minHeight: 30, fontWeight: 650, boxShadow: "none" },
          "& .MuiChip-root": { height: 23, fontSize: 10.5, maxWidth: "100%" },
          "& .MuiInputBase-root": { fontSize: 12 },
          "& .MuiSelect-select": { py: 0.85 },
          "& .MuiInputLabel-root": { fontSize: 12 },
          "& .MuiAlert-root": { py: 0.5, px: 1.25, fontSize: 11.5, borderRadius: 1.5 },
          "& .MuiAlert-message": { py: 0.5 },
          "& .MuiAlert-icon": { fontSize: 18, py: 0.5, mr: 1 },
          "& .MuiTableCell-root": { borderColor: "var(--ops-border)", fontSize: 11.5 },
          "& .MuiTableHead-root .MuiTableCell-root": { bgcolor: "var(--ops-subtle)", color: "var(--ops-muted)", fontWeight: 800 },
        }}>
          {children}
        </Box>
      </Box>
    </OperationsTheme>
  );
}

export function ConfiguracaoPlaceholder({ colors }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.25, md: 1.5 },
        borderRadius: 2,
        bgcolor: colors.card,
        border: `1px solid ${colors.border}`,
      }}
    >
      <Typography sx={{ color: colors.text, fontWeight: 900, mb: 0.6 }}>
        Configuração
      </Typography>
      <Typography sx={{ color: colors.muted, fontSize: 14 }}>
        As opções de configuração da régua de inadimplência serão exibidas aqui.
      </Typography>
    </Paper>
  );
}

// Placeholder kept only as a fallback while the WhatsApp templates view is evolving.
// eslint-disable-next-line no-unused-vars
function CobrancaWhatsappPanel({ colors }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.25, md: 1.5 },
        borderRadius: 2,
        bgcolor: colors.card,
        border: `1px solid ${colors.border}`,
      }}
    >
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1.2} alignItems="center">
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 2,
              bgcolor: colors.green,
              color: "#fff",
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
            }}
          >
            <WhatsApp sx={{ fontSize: 19 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: colors.text, fontWeight: 900 }}>
              Modelos de mensagem
            </Typography>
            <Typography sx={{ color: colors.muted, fontSize: 13 }}>
              Templates do WhatsApp para notificações de inadimplência.
            </Typography>
          </Box>
        </Stack>

        <Paper
          elevation={0}
          sx={{
            px: 2,
            py: 3,
            borderRadius: 1.5,
            bgcolor: colors.cardAlt,
            border: `1px dashed ${colors.border}`,
            textAlign: "center",
          }}
        >
          <Typography sx={{ color: colors.muted, fontSize: 14 }}>
            Nenhum template disponível.
          </Typography>
        </Paper>
      </Stack>
    </Paper>
  );
}

const templateLimit = 50;

const templateStatusLabels = {
  APPROVED: "Aprovado",
  PENDING: "Pendente",
  REJECTED: "Rejeitado",
  PAUSED: "Pausado",
  DISABLED: "Desativado",
  IN_APPEAL: "Em recurso",
};

const templateCategoryLabels = {
  AUTHENTICATION: "Autenticação",
  MARKETING: "Marketing",
  UTILITY: "Utilidade",
};

const templateQualityLabels = {
  GREEN: "Alta",
  YELLOW: "Média",
  RED: "Baixa",
  UNKNOWN: "Pendente",
};

const getTemplateStatusColor = (status) => {
  switch (status) {
    case "APPROVED":
      return "success";
    case "PENDING":
    case "IN_APPEAL":
      return "warning";
    case "REJECTED":
    case "PAUSED":
    case "DISABLED":
      return "error";
    default:
      return "default";
  }
};

const getTemplateQualityColor = (quality) => {
  switch (quality) {
    case "GREEN":
      return "success";
    case "YELLOW":
      return "warning";
    case "RED":
      return "error";
    default:
      return "default";
  }
};

const formatTemplateValue = (value, labels = {}) => {
  if (value === null || value === undefined || value === "") return "--";
  return labels[value] || value;
};

const normalizeTemplateName = (value = "") => (
  value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "")
);

const normalizeTemplateNameInput = (value = "") => (
  value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/^_+/, "")
);

const getTemplateBodyPreview = (components = []) => {
  if (!Array.isArray(components)) return "";
  const body = components.find((component) => component?.type === "BODY")?.text || "";
  return body.length > 100 ? `${body.slice(0, 100)}...` : body;
};

const createTemplateInitialState = {
  category: "UTILITY",
  type: "STANDARD",
  name: "",
  language: "pt_BR",
  header: "",
  body: "",
  footer: "",
  allowCategoryChange: true,
};

const textFieldConfig = {
  header: { label: "Cabeçalho", maxLength: 60 },
  body: { label: "Corpo", maxLength: 1024 },
  footer: { label: "Rodapé", maxLength: 60 },
};

const variableRegex = /\{\{(\d+)\}\}/g;

const extractVariables = (text = "") => {
  const matches = [...String(text).matchAll(variableRegex)];
  return [...new Set(matches.map((match) => Number(match[1])))].sort((a, b) => a - b);
};

const replaceVariablesWithSamples = (text = "", samples = {}) => (
  String(text).replace(variableRegex, (match, variableNumber) => samples[variableNumber] || match)
);

const hasEdgeVariable = (text = "") => {
  const trimmed = String(text).trim();
  return /^\{\{\d+\}\}/.test(trimmed) || /\{\{\d+\}\}$/.test(trimmed);
};

const buildTemplateComponents = (templateState, samples) => {
  const components = [];
  const header = templateState.header.trim();
  const body = templateState.body.trim();
  const footer = templateState.footer.trim();

  if (header) {
    const headerComponent = {
      type: "HEADER",
      format: "TEXT",
      text: header,
    };
    const headerVariables = extractVariables(header);

    if (headerVariables.length > 0) {
      headerComponent.example = {
        header_text: headerVariables.map((number) => samples[number] || ""),
      };
    }

    components.push(headerComponent);
  }

  if (body) {
    const bodyComponent = {
      type: "BODY",
      text: body,
    };
    const bodyVariables = extractVariables(body);

    if (bodyVariables.length > 0) {
      bodyComponent.example = {
        body_text: [bodyVariables.map((number) => samples[number] || "")],
      };
    }

    components.push(bodyComponent);
  }

  if (footer) {
    components.push({
      type: "FOOTER",
      text: footer,
    });
  }

  return components;
};

function CobrancaWhatsappTemplatesPanel({ colors }) {
  const idiomas = [
    { value: "pt_BR", label: "Português (Brasil)" },
    { value: "en_US", label: "Inglês (Estados Unidos)" },
    { value: "es_ES", label: "Espanhol" },
  ];
  const categorias = [
    { value: "UTILITY", label: "Utilidade" },
    { value: "MARKETING", label: "Marketing" },
    { value: "AUTHENTICATION", label: "Autenticação" },
  ];
  const [templates, setTemplates] = useState([]);
  const [afterCursor, setAfterCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createStep, setCreateStep] = useState(0);
  const [templateForm, setTemplateForm] = useState(createTemplateInitialState);
  const [variableSamples, setVariableSamples] = useState({});
  const [sortBy, setSortBy] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const templateColumns = [
    { key: "name", label: "Nome do modelo" },
    { key: "category", label: "Categoria" },
    { key: "language", label: "Idioma" },
    { key: "status", label: "Status" },
    { key: "qualityScore", label: "Qualidade" },
    { key: "preview", label: "Prévia" },
  ];

  const sortedTemplates = useMemo(() => {
    const getSortValue = (template, key) => {
      if (key === "preview") return getTemplateBodyPreview(template.components);
      return template?.[key] || "";
    };

    return [...templates].sort((firstTemplate, secondTemplate) => {
      const firstValue = String(getSortValue(firstTemplate, sortBy)).toLocaleLowerCase("pt-BR");
      const secondValue = String(getSortValue(secondTemplate, sortBy)).toLocaleLowerCase("pt-BR");
      const comparison = firstValue.localeCompare(secondValue, "pt-BR", { numeric: true, sensitivity: "base" });
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [templates, sortBy, sortDirection]);

  const createVariables = useMemo(() => {
    const fields = ["header", "body", "footer"];
    return fields.flatMap((field) => (
      extractVariables(templateForm[field]).map((number) => ({ field, number }))
    ));
  }, [templateForm]);

  const nextVariableNumber = useMemo(() => {
    const allVariables = createVariables.map((variable) => variable.number);
    return allVariables.length > 0 ? Math.max(...allVariables) + 1 : 1;
  }, [createVariables]);

  const invalidVariableFields = useMemo(() => ({
    header: hasEdgeVariable(templateForm.header),
    body: hasEdgeVariable(templateForm.body),
    footer: hasEdgeVariable(templateForm.footer),
  }), [templateForm]);

  const hasMissingVariableSamples = useMemo(() => (
    createVariables.some(({ number }) => !String(variableSamples[number] || "").trim())
  ), [createVariables, variableSamples]);

  const canSubmitTemplate = (
    normalizeTemplateName(templateForm.name) &&
    templateForm.language &&
    templateForm.category &&
    templateForm.body.trim() &&
    !invalidVariableFields.header &&
    !invalidVariableFields.body &&
    !invalidVariableFields.footer &&
    !hasMissingVariableSamples &&
    !saving
  );

  const loadTemplates = useCallback(async ({ after = null, append = false } = {}) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setError("");
    }

    try {
      const response = await cobrancaNotificacoesService.getTemplates({
        limit: templateLimit,
        after,
      });

      setTemplates((current) => (append ? [...current, ...response.data] : response.data));
      setAfterCursor(response.after);
    } catch (err) {
      setError(cobrancaNotificacoesService.getApiErrorMessage(err, "Não foi possível carregar os templates do WhatsApp."));
      if (!append) {
        setTemplates([]);
        setAfterCursor(null);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const setTemplateField = (field, value) => {
    setTemplateForm((current) => ({ ...current, [field]: value }));
    setError("");
    setSuccess("");
  };

  const handleStartCreate = () => {
    setIsCreating(true);
    setCreateStep(0);
    setTemplateForm(createTemplateInitialState);
    setVariableSamples({});
    setError("");
    setSuccess("");
  };

  const handleBackToList = () => {
    setIsCreating(false);
    setCreateStep(0);
    setTemplateForm(createTemplateInitialState);
    setVariableSamples({});
    setError("");
  };

  const handleAddVariable = (field) => {
    const variable = `{{${nextVariableNumber}}}`;
    setTemplateForm((current) => {
      const currentValue = current[field] || "";
      const separator = currentValue && !/\s$/.test(currentValue) ? " " : "";
      return {
        ...current,
        [field]: `${currentValue}${separator}${variable}`,
      };
    });
    setError("");
    setSuccess("");
  };

  const handleSampleChange = (variableNumber) => (event) => {
    setVariableSamples((current) => ({
      ...current,
      [variableNumber]: event.target.value,
    }));
    setError("");
    setSuccess("");
  };

  const handleSortTemplates = (columnKey) => {
    if (sortBy === columnKey) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }

    setSortBy(columnKey);
    setSortDirection("asc");
  };

  const handleCreateTemplate = async () => {
    const normalizedName = normalizeTemplateName(templateForm.name);

    if (!canSubmitTemplate) {
      setError("Preencha nome, corpo e amostras das variáveis antes de enviar.");
      return;
    }

    if (!normalizedName) {
      setError("Informe o nome técnico do template.");
      return;
    }

    if (!templateForm.language) {
      setError("Informe o idioma do template.");
      return;
    }

    if (!templateForm.category) {
      setError("Informe a categoria do template.");
      return;
    }

    if (!templateForm.body.trim()) {
      setError("Informe o corpo do template.");
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      await cobrancaNotificacoesService.createTemplate({
        name: normalizedName,
        language: templateForm.language,
        category: templateForm.category,
        allowCategoryChange: templateForm.allowCategoryChange,
        components: buildTemplateComponents(templateForm, variableSamples),
      });

      setTemplateForm(createTemplateInitialState);
      setCreateStep(0);
      setVariableSamples({});
      setIsCreating(false);
      setSuccess("Template enviado para análise.");
      loadTemplates();
    } catch (err) {
      setError(cobrancaNotificacoesService.getApiErrorMessage(err, "Não foi possível criar o template do WhatsApp."));
    } finally {
      setSaving(false);
    }
  };

  const renderTemplatePreview = () => (
    <Paper elevation={0} sx={{ border: `1px solid ${colors.border}`, borderRadius: 1.5, overflow: "hidden" }}>
      <Box sx={{ px: 1.75, py: 1.25, borderBottom: `1px solid ${colors.border}` }}>
        <Typography sx={{ color: colors.text, fontWeight: 900, fontSize: 13 }}>
          Prévia do modelo
        </Typography>
      </Box>
      <Box sx={{ bgcolor: "#efeae2", minHeight: 300, color: "#111b21" }}>
        <Box
          sx={{
            px: 1.25,
            py: 1,
            bgcolor: "#f0f2f5",
            display: "flex",
            alignItems: "center",
            gap: 1,
            borderBottom: "1px solid #d6dadd",
          }}
        >
          <Box sx={{ width: 30, height: 30, borderRadius: "50%", bgcolor: "#25d366", display: "grid", placeItems: "center", color: "#fff" }}>
            <WhatsApp sx={{ fontSize: 18 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: "#111b21", fontWeight: 700, fontSize: 12, lineHeight: 1.2 }}>
              Cobrança
            </Typography>
            <Typography sx={{ color: "#667781", fontSize: 10, lineHeight: 1.2 }}>
              WhatsApp Business
            </Typography>
          </Box>
        </Box>
        <Box sx={{ p: 1.5, display: "flex", justifyContent: "flex-end", alignItems: "flex-start", minHeight: 245 }}>
        <Paper elevation={0} sx={{ p: 1.25, borderRadius: "8px 0 8px 8px", bgcolor: "#d9fdd3", maxWidth: "94%", boxShadow: "0 1px 1px rgba(0, 0, 0, 0.08)" }}>
          {(templateForm.header || templateForm.name) ? (
            <Typography sx={{ color: "#111b21", fontWeight: 800, fontSize: 12, overflowWrap: "anywhere" }}>
              {replaceVariablesWithSamples(templateForm.header || templateForm.name, variableSamples)}
            </Typography>
          ) : null}
          <Typography sx={{ color: "#111b21", mt: templateForm.header || templateForm.name ? 1 : 0, fontSize: 12, lineHeight: 1.4, whiteSpace: "pre-line", overflowWrap: "anywhere" }}>
            {replaceVariablesWithSamples(templateForm.body || "Digite o corpo da mensagem para visualizar a prévia.", variableSamples)}
          </Typography>
          {templateForm.footer ? (
            <Typography sx={{ display: "block", mt: 1.5, color: "#667781", fontSize: 11, whiteSpace: "pre-line" }}>
              {replaceVariablesWithSamples(templateForm.footer, variableSamples)}
            </Typography>
          ) : null}
          <Typography sx={{ display: "block", mt: 0.5, color: "#667781", textAlign: "right", fontSize: 9 }}>
            10:42
          </Typography>
        </Paper>
        </Box>
      </Box>
    </Paper>
  );

  const renderCreateStepIndicator = () => (
    <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75, mb: 1.5 }}>
      {["Configurar modelo", "Editar modelo", "Enviar para análise"].map((label, index) => (
        <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Typography
            sx={{
              color: index === createStep ? colors.primary : index < createStep ? colors.green : colors.muted,
              fontWeight: index <= createStep ? 800 : 600,
              fontSize: 13,
            }}
          >
            {label}
          </Typography>
          {index < 2 ? (
            <Typography sx={{ color: colors.muted, fontWeight: 800, fontSize: 13 }}>
              {">>"}
            </Typography>
          ) : null}
        </Box>
      ))}
    </Box>
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.25, md: 1.5 },
        borderRadius: 2,
        bgcolor: colors.card,
        border: `1px solid ${colors.border}`,
      }}
    >
      <Stack spacing={1.25}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.4}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.2} alignItems="center">
            {isCreating ? (
              <IconButton size="small" onClick={handleBackToList} aria-label="Voltar para templates" disabled={saving}>
                <ArrowBackRounded fontSize="small" />
              </IconButton>
            ) : null}
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: 2,
                bgcolor: colors.green,
                color: "#fff",
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}
            >
              <WhatsApp sx={{ fontSize: 19 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ color: colors.text, fontWeight: 900 }}>
                {isCreating ? "Criar modelo" : "Modelos de mensagem"}
              </Typography>
              <Typography sx={{ color: colors.muted, fontSize: 13 }}>
                Templates do WhatsApp para notificações de inadimplência
              </Typography>
            </Box>
          </Stack>

          {!isCreating ? (
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<RefreshRounded />}
                onClick={() => loadTemplates()}
                disabled={loading || loadingMore || saving}
                sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 800 }}
              >
                Atualizar
              </Button>
              <Button
                variant="contained"
                startIcon={<AddRounded />}
                onClick={handleStartCreate}
                disabled={saving}
                sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 800 }}
              >
                Criar modelo
              </Button>
            </Stack>
          ) : null}
        </Stack>

        {isCreating ? (
        <Paper
          elevation={0}
          sx={{
            px: 2,
            py: 2,
            borderRadius: 1.5,
            bgcolor: colors.cardAlt,
            border: `1px solid ${colors.border}`,
          }}
        >
          <Stack spacing={1.5}>
            {createStep === 0 ? (
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) 320px" }, gap: 1.5 }}>
                <Paper elevation={0} sx={{ border: `1px solid ${colors.border}`, borderRadius: 1.5, p: { xs: 1.5, sm: 2 }, bgcolor: colors.card }}>
                  {renderCreateStepIndicator()}
                  <Typography sx={{ color: colors.text, fontWeight: 900, mb: 0.5 }}>
                    Configurar seu modelo
                  </Typography>
                  <Typography sx={{ color: colors.muted, fontSize: 13, mb: 1.5 }}>
                    Escolha a categoria que melhor descreve seu modelo de mensagem.
                  </Typography>

                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, gap: 1, mb: 1.5 }}>
                    {categorias.filter((categoria) => categoria.value !== "AUTHENTICATION").map((categoria) => (
                      <Button
                        key={categoria.value}
                        variant={templateForm.category === categoria.value ? "contained" : "outlined"}
                        onClick={() => setTemplateField("category", categoria.value)}
                        sx={{ justifyContent: "center", textTransform: "none", borderRadius: 1.5, py: 0.85 }}
                      >
                        {categoria.label}
                      </Button>
                    ))}
                  </Box>

                  <Paper
                    elevation={0}
                    onClick={() => setTemplateField("type", "STANDARD")}
                    sx={{
                      p: 1.25,
                      border: `1px solid ${colors.primary}`,
                      bgcolor: colors.cardAlt,
                      borderRadius: 1.5,
                      cursor: "pointer",
                    }}
                  >
                    <Typography sx={{ color: colors.text, fontWeight: 900, fontSize: 13 }}>
                      Padrão
                    </Typography>
                    <Typography sx={{ color: colors.muted, fontSize: 12 }}>
                      Envie mensagens com mídia, cabeçalho, rodapé e botões personalizados.
                    </Typography>
                  </Paper>

                  <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
                    <Button variant="outlined" onClick={handleBackToList} sx={{ textTransform: "none", borderRadius: 1.5, fontWeight: 800 }}>
                      Descartar
                    </Button>
                    <Button variant="contained" onClick={() => setCreateStep(1)} sx={{ textTransform: "none", borderRadius: 1.5, fontWeight: 800 }}>
                      Avançar
                    </Button>
                  </Box>
                </Paper>
                {renderTemplatePreview()}
              </Box>
            ) : null}

            {createStep === 1 ? (
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) 320px" }, gap: 1.5 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Paper elevation={0} sx={{ border: `1px solid ${colors.border}`, borderRadius: 1.5, p: { xs: 1.5, sm: 2 }, bgcolor: colors.card }}>
            {renderCreateStepIndicator()}
            <Typography sx={{ color: colors.text, fontWeight: 900, fontSize: 14 }}>
              Nome e idioma do modelo
            </Typography>
            <Stack direction={{ xs: "column", lg: "row" }} spacing={1.4} alignItems={{ xs: "stretch", lg: "flex-start" }}>
              <TextField
                label="Nome do modelo"
                value={templateForm.name}
                onChange={(event) => setTemplateField("name", normalizeTemplateNameInput(event.target.value))}
                helperText="Ex: cobranca_primeiro_aviso"
                size="small"
                disabled={saving}
                sx={{ minWidth: { lg: 260 } }}
              />
              <TextField
                select
                label="Idioma"
                value={templateForm.language}
                onChange={(event) => setTemplateField("language", event.target.value)}
                helperText="Obrigatório"
                size="small"
                disabled={saving}
                sx={{ minWidth: { lg: 210 } }}
              >
                {idiomas.map((idioma) => (
                  <MenuItem key={idioma.value} value={idioma.value}>
                    {idioma.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Categoria"
                value={templateForm.category}
                onChange={(event) => setTemplateField("category", event.target.value)}
                helperText="Obrigatória"
                size="small"
                disabled={saving}
                sx={{ display: "none" }}
              >
                {categorias.map((categoria) => (
                  <MenuItem key={categoria.value} value={categoria.value}>
                    {categoria.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            </Paper>
            <Paper elevation={0} sx={{ border: `1px solid ${colors.border}`, borderRadius: 1.5, p: { xs: 1.5, sm: 2 }, bgcolor: colors.card }}>
              <Typography sx={{ color: colors.text, fontWeight: 900, fontSize: 14, mb: 0.5 }}>
                Conteúdo
              </Typography>
              <Typography sx={{ color: colors.muted, fontSize: 13, mb: 1.5 }}>
                Adicione cabeçalho, corpo de texto e rodapé para seu modelo.
              </Typography>
              <Stack spacing={1.35}>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 0.5 }}>
                <Typography sx={{ color: colors.text, fontWeight: 800, fontSize: 13 }}>
                  Cabeçalho · Opcional
                </Typography>
                <Button size="small" variant="text" startIcon={<AddRounded />} onClick={() => handleAddVariable("header")} sx={{ textTransform: "none", borderRadius: 1.5, fontWeight: 800 }}>
                  Adicionar variável
                </Button>
              </Box>
              <TextField
                value={templateForm.header}
                onChange={(event) => setTemplateField("header", event.target.value)}
                placeholder="Adicione uma pequena linha de texto ao cabeçalho"
                size="small"
                fullWidth
                error={invalidVariableFields.header}
                helperText={invalidVariableFields.header ? "As variáveis não podem estar no início ou no fim do campo." : `${templateForm.header.length}/${textFieldConfig.header.maxLength}`}
                inputProps={{ maxLength: textFieldConfig.header.maxLength }}
                disabled={saving}
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: -1 }}>
              <Typography sx={{ color: colors.text, fontWeight: 800, fontSize: 13 }}>
                Corpo
              </Typography>
              <Button size="small" variant="text" startIcon={<AddRounded />} onClick={() => handleAddVariable("body")} sx={{ textTransform: "none", borderRadius: 1.5, fontWeight: 800 }}>
                Adicionar variável
              </Button>
            </Box>
            <TextField
              label="Corpo da mensagem"
              value={templateForm.body}
              onChange={(event) => setTemplateField("body", event.target.value)}
              placeholder="Olá {{1}}, identificamos uma parcela em atraso."
              helperText={invalidVariableFields.body ? "As variáveis não podem estar no início ou no fim do campo." : `${templateForm.body.length}/${textFieldConfig.body.maxLength}`}
              error={invalidVariableFields.body}
              multiline
              minRows={5}
              size="small"
              disabled={saving}
              inputProps={{ maxLength: textFieldConfig.body.maxLength }}
              fullWidth
            />
            {createVariables.length > 0 ? (
              <Paper elevation={0} sx={{ bgcolor: colors.cardAlt, borderRadius: 1.5, p: { xs: 1.5, sm: 2 }, border: `1px solid ${colors.border}` }}>
                <Typography sx={{ color: colors.text, fontWeight: 900, fontSize: 13, mb: 0.5 }}>
                  Amostras de variáveis
                </Typography>
                <Typography sx={{ color: colors.muted, fontSize: 12, mb: 1.5 }}>
                  Adicione um exemplo para cada variável. Esses valores são usados apenas na análise do modelo e na prévia.
                </Typography>
                <Stack spacing={1}>
                  {createVariables.map(({ field, number }) => (
                    <Box key={`${field}-${number}`} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "110px minmax(0, 1fr)" }, gap: 1, alignItems: "flex-start" }}>
                      <TextField value={`{{${number}}}`} size="small" disabled helperText={textFieldConfig[field].label} />
                      <TextField
                        value={variableSamples[number] || ""}
                        onChange={handleSampleChange(number)}
                        size="small"
                        fullWidth
                        error={!variableSamples[number]}
                        placeholder={`Insira conteúdo para {{${number}}}`}
                        helperText={!variableSamples[number] ? "Adicionar texto de amostra" : " "}
                      />
                    </Box>
                  ))}
                </Stack>
              </Paper>
            ) : null}

            <Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 0.5 }}>
                <Typography sx={{ color: colors.text, fontWeight: 800, fontSize: 13 }}>
                  Rodapé · Opcional
                </Typography>
                <Button size="small" variant="text" startIcon={<AddRounded />} onClick={() => handleAddVariable("footer")} sx={{ textTransform: "none", borderRadius: 1.5, fontWeight: 800 }}>
                  Adicionar variável
                </Button>
              </Box>
              <TextField
                value={templateForm.footer}
                onChange={(event) => setTemplateField("footer", event.target.value)}
                placeholder="Adicione uma pequena linha de texto ao rodapé"
                size="small"
                fullWidth
                error={invalidVariableFields.footer}
                helperText={invalidVariableFields.footer ? "As variáveis não podem estar no início ou no fim do campo." : `${templateForm.footer.length}/${textFieldConfig.footer.maxLength}`}
                inputProps={{ maxLength: textFieldConfig.footer.maxLength }}
                disabled={saving}
              />
            </Box>
            </Stack>
            </Paper>

            <Stack direction="row" spacing={1} justifyContent="space-between">
              <Button
                variant="outlined"
                onClick={() => setCreateStep(0)}
                disabled={saving}
                sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 800 }}
              >
                Voltar
              </Button>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress color="inherit" size={17} /> : <AddRounded />}
                onClick={handleCreateTemplate}
                disabled={!canSubmitTemplate}
                sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 800 }}
              >
                Enviar para análise
              </Button>
            </Stack>
              </Box>
              {renderTemplatePreview()}
              </Box>
            ) : null}
          </Stack>
        </Paper>
        ) : null}

        {error ? (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {error}
          </Alert>
        ) : null}

        {success && !isCreating ? (
          <Alert severity="success" sx={{ borderRadius: 2 }} onClose={() => setSuccess("")}>
            {success}
          </Alert>
        ) : null}

        {!isCreating && loading ? (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ color: colors.muted }}>
            <CircularProgress size={18} />
            <Typography sx={{ fontSize: 13 }}>Carregando templates...</Typography>
          </Stack>
        ) : null}

        {!isCreating && !loading && !templates.length ? (
          <Paper
            elevation={0}
            sx={{
              px: 2,
              py: 3,
              borderRadius: 1.5,
              bgcolor: colors.cardAlt,
              border: `1px dashed ${colors.border}`,
              textAlign: "center",
            }}
          >
            <Typography sx={{ color: colors.muted, fontSize: 14 }}>
              Nenhum template cadastrado.
            </Typography>
          </Paper>
        ) : null}

        {!isCreating && !loading && templates.length ? (
          <Stack spacing={1.2}>
            <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${colors.border}`, borderRadius: 1.5 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {templateColumns.map((column) => (
                      <TableCell key={column.key} sortDirection={sortBy === column.key ? sortDirection : false} sx={{ fontWeight: 800 }}>
                        <TableSortLabel
                          active={sortBy === column.key}
                          direction={sortBy === column.key ? sortDirection : "asc"}
                          onClick={() => handleSortTemplates(column.key)}
                        >
                          {column.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedTemplates.map((template) => (
                    <TableRow key={template.id || `${template.name}-${template.language}`} hover>
                      <TableCell>
                        <Typography sx={{ fontWeight: 800, fontSize: 13, overflowWrap: "anywhere" }}>
                          {template.name || "Template sem nome"}
                        </Typography>
                        <Typography sx={{ color: colors.muted, fontSize: 11 }}>
                          {template.id ? `ID ${template.id}` : ""}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatTemplateValue(template.category, templateCategoryLabels)}</TableCell>
                      <TableCell>{formatTemplateValue(template.language)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={formatTemplateValue(template.status, templateStatusLabels)}
                          color={getTemplateStatusColor(template.status)}
                          sx={{ fontWeight: 800 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={formatTemplateValue(template.qualityScore, templateQualityLabels)}
                          color={getTemplateQualityColor(template.qualityScore)}
                          variant={template.qualityScore ? "filled" : "outlined"}
                          sx={{ fontWeight: 800 }}
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 360 }}>
                        <Typography sx={{ color: colors.muted, fontSize: 13, overflowWrap: "anywhere" }}>
                          {getTemplateBodyPreview(template.components) || "--"}
                        </Typography>
                        {template.rejectedReason ? (
                          <Typography sx={{ color: colors.red, fontSize: 12, mt: 0.5, overflowWrap: "anywhere" }}>
                            {template.rejectedReason}
                          </Typography>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }} justifyContent="space-between">
              <Typography sx={{ color: colors.muted, fontSize: 12 }}>
                {templates.length} modelo{templates.length === 1 ? "" : "s"} carregado{templates.length === 1 ? "" : "s"}.
              </Typography>

              {afterCursor ? (
                <Button
                  variant="outlined"
                  onClick={() => loadTemplates({ after: afterCursor, append: true })}
                  disabled={loadingMore || saving}
                  sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 800 }}
                >
                  {loadingMore ? "Carregando..." : "Carregar mais"}
                </Button>
              ) : null}
            </Stack>
          </Stack>
        ) : null}
      </Stack>
    </Paper>
  );
}

const serializeCobrancaRegras = (items) =>
  JSON.stringify(
    items
      .map((regra) => ({
        diasAtraso: Number(regra.diasAtraso),
        templateWhatsappNome: regra.templateWhatsappNome.trim(),
        templateWhatsappIdioma: regra.templateWhatsappIdioma.trim(),
      }))
      .sort((a, b) => a.diasAtraso - b.diasAtraso)
  );

// Esta cópia preserva a tela atual enquanto Configuração evolui separadamente.
function CobrancaAvisosPanel({ colors }) {
  const defaultIdioma = "pt_BR";
  const emptyRegra = {
    diasAtraso: "",
    templateWhatsappNome: "",
    templateWhatsappIdioma: defaultIdioma,
  };
  const [regras, setRegras] = useState([]);
  const [originalRegras, setOriginalRegras] = useState([]);
  const [novaRegra, setNovaRegra] = useState(emptyRegra);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [templatesError, setTemplatesError] = useState("");
  const [resumoElegiveis, setResumoElegiveis] = useState([]);
  const [resumoLoading, setResumoLoading] = useState(false);
  const [resumoError, setResumoError] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const [editingRegra, setEditingRegra] = useState(null);
  const [editError, setEditError] = useState("");

  const hasListChanges = useMemo(
    () => serializeCobrancaRegras(regras) !== serializeCobrancaRegras(originalRegras),
    [regras, originalRegras]
  );
  const hasDraftFields = Boolean(
    novaRegra.diasAtraso || novaRegra.templateWhatsappNome.trim() || novaRegra.templateWhatsappIdioma !== defaultIdioma
  );
  const selectedTemplateId = useMemo(() => (
    templates.find((template) => (
      template.name === novaRegra.templateWhatsappNome
      && template.language === novaRegra.templateWhatsappIdioma
    ))?.id || ""
  ), [novaRegra.templateWhatsappIdioma, novaRegra.templateWhatsappNome, templates]);
  const resumoPorDia = useMemo(() => (
    new Map(resumoElegiveis.map((resumo) => [Number(resumo.diasAtraso), resumo]))
  ), [resumoElegiveis]);

  useEffect(() => {
    let active = true;

    const loadRegras = async () => {
      setLoading(true);
      setError("");

      try {
        const data = await cobrancaNotificacoesService.getRegras();
        if (active) {
          setRegras(data);
          setOriginalRegras(data);
        }
      } catch (err) {
        if (active) {
          setError(cobrancaNotificacoesService.getApiErrorMessage(err, "Não foi possível carregar as regras de notificação."));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadRegras();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (loading) return undefined;

    const diasAtraso = regras.map((regra) => Number(regra.diasAtraso));

    if (!diasAtraso.length) {
      setResumoElegiveis([]);
      setResumoError("");
      setResumoLoading(false);
      return undefined;
    }

    let active = true;

    const loadResumoElegiveis = async () => {
      setResumoLoading(true);
      setResumoError("");

      try {
        const resumo = await cobrancaNotificacoesService.getElegiveisResumo(diasAtraso);
        if (active) {
          setResumoElegiveis(resumo);
        }
      } catch (err) {
        if (active) {
          setResumoElegiveis([]);
          setResumoError(cobrancaNotificacoesService.getApiErrorMessage(err, "Não foi possível carregar o total de envios."));
        }
      } finally {
        if (active) {
          setResumoLoading(false);
        }
      }
    };

    loadResumoElegiveis();

    return () => {
      active = false;
    };
  }, [loading, regras]);

  useEffect(() => {
    let active = true;

    const loadTemplates = async () => {
      setTemplatesLoading(true);
      setTemplatesError("");

      try {
        const allTemplates = [];
        const seenCursors = new Set();
        let after = null;

        do {
          const response = await cobrancaNotificacoesService.getTemplates({ limit: 100, after });
          allTemplates.push(...response.data);
          after = response.after;
        } while (after && !seenCursors.has(after) && seenCursors.add(after));

        if (active) {
          setTemplates(allTemplates);
        }
      } catch (err) {
        if (active) {
          setTemplatesError(cobrancaNotificacoesService.getApiErrorMessage(err, "Não foi possível carregar os modelos do WhatsApp."));
        }
      } finally {
        if (active) {
          setTemplatesLoading(false);
        }
      }
    };

    loadTemplates();

    return () => {
      active = false;
    };
  }, []);

  const setField = (field, value) => {
    setNovaRegra((current) => ({ ...current, [field]: value }));
    setError("");
    setSaved(false);
  };

  const validateRegra = (regra, currentRegras = regras) => {
    const parsedDay = Number(regra.diasAtraso);

    if (!Number.isInteger(parsedDay) || parsedDay < 1 || parsedDay > 730) {
      return "Informe dias de atraso entre 1 e 730.";
    }

    if (currentRegras.some((item) => Number(item.diasAtraso) === parsedDay)) {
      return "Não é permitido cadastrar dias de atraso duplicados.";
    }

    if (!regra.templateWhatsappNome.trim()) {
      return "Informe o nome do template do WhatsApp.";
    }

    if (!regra.templateWhatsappIdioma.trim()) {
      return "Informe o idioma do template do WhatsApp.";
    }

    return "";
  };

  const handleAddRegra = () => {
    const validationMessage = validateRegra(novaRegra);

    if (validationMessage) {
      setError(validationMessage);
      return;
    }

    const selectedTemplate = templates.find((template) => template.id === selectedTemplateId);

    if (!selectedTemplate) {
      setError("Selecione um modelo de WhatsApp.");
      return;
    }

    const nextRegra = {
      diasAtraso: Number(novaRegra.diasAtraso),
      templateWhatsappNome: novaRegra.templateWhatsappNome.trim(),
      templateWhatsappIdioma: novaRegra.templateWhatsappIdioma.trim(),
    };

    setRegras((current) => [...current, nextRegra].sort((a, b) => a.diasAtraso - b.diasAtraso));
    setNovaRegra(emptyRegra);
    setError("");
    setSaved(false);
  };

  const handleRemoveRegra = (day) => {
    setRegras((current) => current.filter((item) => Number(item.diasAtraso) !== Number(day)));
    setError("");
    setSaved(false);
  };

  const handleStartEditRegra = (regra) => {
    setEditingRegra({
      originalDay: Number(regra.diasAtraso),
      diasAtraso: String(regra.diasAtraso),
      templateWhatsappNome: regra.templateWhatsappNome,
      templateWhatsappIdioma: regra.templateWhatsappIdioma || defaultIdioma,
    });
    setEditError("");
  };

  const editSelectedTemplateId = useMemo(() => (
    templates.find((template) => (
      template.name === editingRegra?.templateWhatsappNome
      && template.language === editingRegra?.templateWhatsappIdioma
    ))?.id || ""
  ), [editingRegra?.templateWhatsappIdioma, editingRegra?.templateWhatsappNome, templates]);

  const handleEditTemplateChange = (templateId) => {
    const selectedTemplate = templates.find((template) => template.id === templateId);

    if (!selectedTemplate) return;

    setEditingRegra((current) => ({
      ...current,
      templateWhatsappNome: selectedTemplate.name,
      templateWhatsappIdioma: selectedTemplate.language || defaultIdioma,
    }));
    setEditError("");
  };

  const handleUpdateRegra = () => {
    if (!editingRegra) return;

    const currentRegras = regras.filter((regra) => Number(regra.diasAtraso) !== editingRegra.originalDay);
    const validationMessage = validateRegra(editingRegra, currentRegras);

    if (validationMessage) {
      setEditError(validationMessage);
      return;
    }

    const selectedTemplate = templates.find((template) => template.id === editSelectedTemplateId);

    if (!selectedTemplate) {
      setEditError("Selecione um modelo de WhatsApp.");
      return;
    }

    setRegras((current) => current
      .map((regra) => (
        Number(regra.diasAtraso) === editingRegra.originalDay
          ? {
            ...regra,
            diasAtraso: Number(editingRegra.diasAtraso),
            templateWhatsappNome: editingRegra.templateWhatsappNome.trim(),
            templateWhatsappIdioma: editingRegra.templateWhatsappIdioma.trim(),
          }
          : regra
      ))
      .sort((first, second) => Number(first.diasAtraso) - Number(second.diasAtraso)));
    setEditingRegra(null);
    setEditError("");
    setError("");
    setSaved(false);
  };

  const handleTemplateChange = (templateId) => {
    const selectedTemplate = templates.find((template) => template.id === templateId);

    if (!selectedTemplate) return;

    setNovaRegra((current) => ({
      ...current,
      templateWhatsappNome: selectedTemplate.name,
      templateWhatsappIdioma: selectedTemplate.language || defaultIdioma,
    }));
    setError("");
    setSaved(false);
  };

  const validateRegras = () => {
    const seenDays = new Set();

    for (const regra of regras) {
      const validationMessage = validateRegra(regra, []);
      const parsedDay = Number(regra.diasAtraso);

      if (validationMessage) {
        return validationMessage;
      }

      if (seenDays.has(parsedDay)) {
        return "Não é permitido cadastrar dias de atraso duplicados.";
      }

      seenDays.add(parsedDay);
    }

    return "";
  };

  const handleSave = async ({ confirmedClear = false } = {}) => {
    if (hasDraftFields) {
      setError("Voce preencheu uma regra, mas ainda nao clicou em Adicionar regra.");
      setSaved(false);
      return;
    }

    if (!hasListChanges) {
      return;
    }

    const validationMessage = validateRegras();

    if (validationMessage) {
      setError(validationMessage);
      setSaved(false);
      return;
    }

    if (!confirmedClear && originalRegras.length > 0 && regras.length === 0) {
      setConfirmClearOpen(true);
      return;
    }

    setSaving(true);
    setError("");
    setSaved(false);

    try {
      const savedRegras = await cobrancaNotificacoesService.saveRegras(regras);
      setRegras(savedRegras);
      setOriginalRegras(savedRegras);
      setSaved(true);
    } catch (err) {
      setError(cobrancaNotificacoesService.getApiErrorMessage(err, "Não foi possível salvar as regras de notificação de cobrança."));
      setSaved(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.25, md: 1.5 },
        borderRadius: 2,
        bgcolor: colors.card,
        border: `1px solid ${colors.border}`,
      }}
    >
      <Stack spacing={1.25}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.4}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.2} alignItems="center">
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: 2,
                bgcolor: colors.green,
                color: "#fff",
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}
            >
              <WhatsApp sx={{ fontSize: 19 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ color: colors.text, fontWeight: 900 }}>
                Envio de mensagens por WhatsApp
              </Typography>
              <Typography sx={{ color: colors.muted, fontSize: 13 }}>
                Regras de disparo automático por dias de atraso
              </Typography>
            </Box>
          </Stack>

          {hasListChanges || saving ? (
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress color="inherit" size={17} /> : <SaveRounded />}
              onClick={handleSave}
              disabled={loading || saving}
              sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 800 }}
            >
              Salvar
            </Button>
          ) : null}
        </Stack>

        <Stack direction={{ xs: "column", md: "row" }} spacing={1.4} alignItems={{ xs: "stretch", md: "flex-start" }}>
          <TextField
            label="Dias em atraso"
            value={novaRegra.diasAtraso}
            onChange={(event) => setField("diasAtraso", event.target.value.replace(/\D/g, ""))}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                handleAddRegra();
              }
            }}
            type="text"
            inputMode="numeric"
            helperText="Entre 1 e 730"
            sx={{ maxWidth: { md: 170 } }}
            size="small"
            disabled={loading || saving}
          />
          <TextField
            select
            label="Template WhatsApp"
            value={selectedTemplateId}
            onChange={(event) => handleTemplateChange(event.target.value)}
            helperText={templatesError || (templatesLoading ? "Carregando modelos..." : "Status exibido conforme o modelo no WhatsApp")}
            sx={{ minWidth: { md: 280 } }}
            size="small"
            disabled={loading || templatesLoading || saving || Boolean(templatesError)}
          >
            <MenuItem value="" disabled>
              Selecione um modelo
            </MenuItem>
            {!templatesLoading && !templates.length ? (
              <MenuItem value="" disabled>
                Nenhum modelo encontrado
              </MenuItem>
            ) : null}
            {templates.map((template) => {
              const statusLabel = template.status === "APPROVED" ? "Aprovado" : template.status === "PENDING" ? "Em análise" : template.status || "Indisponível";

              return (
                <MenuItem key={template.id} value={template.id}>
                  {template.name} ({template.language}) - {statusLabel}
                </MenuItem>
              );
            })}
          </TextField>
          <TextField
            label="Idioma do modelo"
            value={novaRegra.templateWhatsappIdioma}
            helperText="Preenchido conforme o modelo selecionado"
            sx={{ minWidth: { md: 220 } }}
            size="small"
            disabled
          />
          <Button
            variant="outlined"
            startIcon={<AddRounded />}
            onClick={handleAddRegra}
            disabled={loading || templatesLoading || saving || Boolean(templatesError)}
            sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 800, minHeight: 40 }}
          >
            Adicionar regra
          </Button>
        </Stack>

        {error ? (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ color: colors.muted }}>
            <CircularProgress size={18} />
            <Typography sx={{ fontSize: 13 }}>Carregando regras...</Typography>
          </Stack>
        ) : null}

        {!loading && !regras.length ? (
          <Paper
            elevation={0}
            sx={{
              p: 1.4,
              borderRadius: 1.5,
              bgcolor: colors.cardAlt,
              border: `1px dashed ${colors.border}`,
            }}
          >
            <Typography sx={{ color: colors.muted, fontSize: 13 }}>
              Nenhuma regra cadastrada. Salve sem regras para remover todos os disparos ativos.
            </Typography>
          </Paper>
        ) : null}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))" },
            gap: 1.2,
          }}
        >
          {regras.map((regra) => (
            (() => {
              const resumoDia = resumoPorDia.get(Number(regra.diasAtraso));
              const totalClientes = Number(resumoDia?.totalClientes || 0);

              return (
            <Paper
              key={`${regra.id || "novo"}-${regra.diasAtraso}`}
              elevation={0}
              sx={{
                p: 1.4,
                borderRadius: 1.5,
                bgcolor: colors.cardAlt,
                border: `1px solid ${colors.border}`,
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ color: colors.text, fontWeight: 900 }}>
                  {regra.diasAtraso} {regra.diasAtraso === 1 ? "dia" : "dias"}
                </Typography>
                <Typography sx={{ color: colors.softMuted, fontSize: 12 }}>
                  Após o vencimento
                </Typography>
                <Typography sx={{ color: colors.text, fontSize: 13, mt: 1, overflowWrap: "anywhere" }}>
                  {regra.templateWhatsappNome}
                </Typography>
                <Typography sx={{ color: colors.softMuted, fontSize: 12 }}>
                  Idioma: {regra.templateWhatsappIdioma}
                </Typography>
                <Box sx={{ borderTop: `1px solid ${colors.border}`, mt: 1.25, pt: 1.1 }}>
                  <Typography sx={{ color: colors.muted, fontSize: 12 }}>
                    {resumoLoading
                      ? "Calculando contatos elegíveis..."
                      : resumoError
                        ? resumoError
                        : `${formatNumber(totalClientes)} ${totalClientes === 1 ? "contato receberá" : "contatos receberão"} mensagem neste dia de atraso`}
                  </Typography>
                </Box>
              </Box>
              <Stack direction="row" spacing={0.25}>
                <IconButton
                  aria-label={`Editar regra com ${regra.diasAtraso} ${regra.diasAtraso === 1 ? "dia" : "dias"} de atraso`}
                  onClick={() => handleStartEditRegra(regra)}
                  size="small"
                  disabled={saving || templatesLoading || Boolean(templatesError)}
                  sx={{ color: colors.primary }}
                >
                  <EditRounded fontSize="small" />
                </IconButton>
                <IconButton
                  aria-label={`Remover regra com ${regra.diasAtraso} ${regra.diasAtraso === 1 ? "dia" : "dias"} de atraso`}
                  onClick={() => handleRemoveRegra(regra.diasAtraso)}
                  size="small"
                  disabled={saving}
                  sx={{ color: colors.red }}
                >
                  <DeleteOutlineRounded fontSize="small" />
                </IconButton>
              </Stack>
            </Paper>
              );
            })()
          ))}
        </Box>

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {regras.map((regra) => (
            <Chip
              key={`chip-${regra.id || "novo"}-${regra.diasAtraso}`}
              label={`${regra.diasAtraso} ${regra.diasAtraso === 1 ? "dia" : "dias"}`}
              color="primary"
              variant="outlined"
              size="small"
              sx={{ fontWeight: 800 }}
            />
          ))}
        </Stack>

        {saved ? (
          <Alert severity="success" sx={{ borderRadius: 2 }}>
            Configuração salva.
          </Alert>
        ) : null}
      </Stack>

      <Dialog open={Boolean(editingRegra)} onClose={() => setEditingRegra(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Editar regra de notificação</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <TextField
              label="Dias em atraso"
              value={editingRegra?.diasAtraso || ""}
              onChange={(event) => {
                setEditingRegra((current) => ({ ...current, diasAtraso: event.target.value.replace(/\D/g, "") }));
                setEditError("");
              }}
              type="text"
              inputMode="numeric"
              helperText="Entre 1 e 730"
              size="small"
              disabled={saving}
              fullWidth
            />
            <TextField
              select
              label="Template WhatsApp"
              value={editSelectedTemplateId}
              onChange={(event) => handleEditTemplateChange(event.target.value)}
              helperText={templatesError || (templatesLoading ? "Carregando modelos..." : "Status exibido conforme o modelo no WhatsApp")}
              size="small"
              disabled={saving || templatesLoading || Boolean(templatesError)}
              fullWidth
            >
              <MenuItem value="" disabled>
                Selecione um modelo
              </MenuItem>
              {templates.map((template) => {
                const statusLabel = template.status === "APPROVED" ? "Aprovado" : template.status === "PENDING" ? "Em análise" : template.status || "Indisponível";

                return (
                  <MenuItem key={template.id} value={template.id}>
                    {template.name} ({template.language}) - {statusLabel}
                  </MenuItem>
                );
              })}
            </TextField>
            <TextField
              label="Idioma do modelo"
              value={editingRegra?.templateWhatsappIdioma || ""}
              helperText="Preenchido conforme o modelo selecionado"
              size="small"
              disabled
              fullWidth
            />
            {editError ? <Alert severity="error">{editError}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditingRegra(null)} disabled={saving} sx={{ textTransform: "none", fontWeight: 800 }}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleUpdateRegra} disabled={saving || templatesLoading || Boolean(templatesError)} sx={{ textTransform: "none", fontWeight: 800 }}>
            Salvar alteração
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmClearOpen} onClose={() => setConfirmClearOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Remover todas as regras?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: colors.muted, fontSize: 14 }}>
            Ao salvar sem nenhuma regra, todos os disparos automáticos de cobrança por WhatsApp serão removidos.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmClearOpen(false)} sx={{ textTransform: "none", fontWeight: 800 }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              setConfirmClearOpen(false);
              handleSave({ confirmedClear: true });
            }}
            sx={{ textTransform: "none", fontWeight: 800 }}
          >
            Remover regras
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

function KpiCard({ label, value, helper, icon: Icon, variant = "blue" }) {
  const variantColors = {
    red: "var(--ops-danger)",
    orange: "var(--ops-warning)",
    green: "var(--ops-success)",
    teal: "var(--ops-chart-teal)",
    purple: "var(--ops-chart-medium)",
    navy: "var(--ops-heading)",
    blue: "var(--ops-accent)",
  };
  const color = variantColors[variant] || variantColors.blue;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.2,
        minWidth: 0,
        minHeight: 90,
        height: "100%",
        border: "1px solid var(--ops-border)",
        borderTop: `2px solid ${color}`,
        bgcolor: "var(--ops-surface)",
        borderRadius: 1.8,
        boxShadow: "0 1px 3px rgba(31, 58, 86, 0.035)",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, position: "relative" }}>
        <Icon sx={{ fontSize: 16, color, flexShrink: 0 }} />
        <Typography
          sx={{
            color: "var(--ops-muted)",
            fontSize: 11,
            fontWeight: 750,
            lineHeight: 1.2,
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {label}
        </Typography>
      </Stack>
      <Typography
        sx={{
          mt: 0.45,
          fontSize: { xs: 18, md: 20, lg: 16 },
          fontWeight: 850,
          color: "var(--ops-heading)",
          letterSpacing: "-0.02em",
          position: "relative",
          lineHeight: 1.12,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {value}
      </Typography>
      {helper ? (
        <Typography
          sx={{
            mt: 0.2,
            fontSize: 10.5,
            color: "var(--ops-muted)",
            position: "relative",
            lineHeight: 1.2,
          }}
        >
          {helper}
        </Typography>
      ) : null}
    </Paper>
  );
}

function ChartPanel({ title, subtitle, children, colors }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.25, md: 1.5 },
        borderRadius: 2,
        bgcolor: colors.card,
        border: `1px solid ${colors.border}`,
        minWidth: 0,
        boxShadow: "0 1px 3px rgba(31, 58, 86, 0.035)",
      }}
    >
      <Box sx={{ mb: 1 }}>
        <Typography sx={{ color: "var(--ops-heading)", fontWeight: 800, fontSize: 15, lineHeight: 1.25 }}>{title}</Typography>
        {subtitle ? <Typography sx={{ color: "var(--ops-muted)", fontSize: 11, lineHeight: 1.25 }}>{subtitle}</Typography> : null}
      </Box>
      {children}
    </Paper>
  );
}

function EmptyChart({ colors }) {
  return (
    <Box sx={{ height: 260, display: "grid", placeItems: "center", color: colors.softMuted, fontWeight: 800 }}>
      Sem dados para exibir
    </Box>
  );
}

function formatDateReference(value) {
  if (!value) return "-";

  const [year, month, day] = String(value).split("-");
  if (!year || !month || !day) return value;

  return `${day}/${month}/${year}`;
}

function formatShortDateReference(value) {
  if (!value) return "-";

  const [, month, day] = String(value).split("-");
  if (!month || !day) return value;

  return `${day}/${month}`;
}

function formatMonthReference(item) {
  const value = item?.anoMes || item?.dataReferencia;
  if (!value) return "-";

  const [year, month] = String(value).split("-");
  if (!year || !month) return value;

  return `${month}/${year}`;
}

function normalizeEvolutionData(data = [], period = "diaria") {
  return [...data]
    .sort((a, b) => String(a?.dataReferencia || a?.anoMes || "").localeCompare(String(b?.dataReferencia || b?.anoMes || "")))
    .map((item) => ({
      ...item,
      label: period === "mensal" ? formatMonthReference(item) : formatShortDateReference(item?.dataReferencia),
      tooltipLabel: period === "mensal" ? formatMonthReference(item) : formatDateReference(item?.dataReferencia),
      valorInadimplente: Number(item?.valorInadimplente || 0),
      titulosInadimplentes: Number(item?.titulosInadimplentes || 0),
      parcelasInadimplentes: Number(item?.parcelasInadimplentes || 0),
      clientesInadimplentes: Number(item?.clientesInadimplentes || 0),
      atrasoMedio: Number(item?.atrasoMedio || 0),
    }));
}

function EvolutionTooltip({ active, payload, label, colors }) {
  if (!active || !payload?.length) return null;

  const data = payload[0]?.payload || {};

  return (
    <Paper elevation={0} sx={{ p: 1.2, borderRadius: 1.5, border: `1px solid ${colors.border}` }}>
      <Typography sx={{ fontSize: 12, fontWeight: 900, color: colors.text }}>{data.tooltipLabel || label}</Typography>
      <Typography sx={{ fontSize: 12, color: colors.chart.red }}>
        Valor: {formatCurrency(data.valorInadimplente)}
      </Typography>
      <Typography sx={{ fontSize: 12, color: colors.muted }}>
        Clientes: {formatNumber(data.clientesInadimplentes)} | Títulos: {formatNumber(data.titulosInadimplentes)}
      </Typography>
      <Typography sx={{ fontSize: 12, color: colors.muted }}>
        Parcelas: {formatNumber(data.parcelasInadimplentes)} | Atraso médio: {formatAverageDays(data.atrasoMedio)}
      </Typography>
    </Paper>
  );
}

function EvolutionChart({ data, colors, showCountLines = false }) {
  if (!data.length) return <EmptyChart colors={colors} />;

  return (
    <Box sx={{ height: 320 }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 8, right: 8, left: 4, bottom: showCountLines ? 8 : 18 }}>
          <CartesianGrid stroke={colors.border} strokeDasharray="3 4" vertical={false} />
          <XAxis dataKey="label" tick={{ fill: colors.softMuted, fontSize: 11 }} axisLine={false} tickLine={false} />
          <YAxis
            yAxisId="valor"
            tickFormatter={formatCompactCurrency}
            tick={{ fill: colors.softMuted, fontSize: 11 }}
            axisLine={false}
            tickLine={false}
          />
          {showCountLines ? (
            <YAxis yAxisId="quantidade" hide />
          ) : (
            <YAxis
              yAxisId="dias"
              orientation="right"
              tickFormatter={(value) => `${formatNumber(value, { maximumFractionDigits: 0 })}d`}
              tick={{ fill: colors.softMuted, fontSize: 11 }}
              axisLine={false}
              tickLine={false}
            />
          )}
          <Tooltip content={<EvolutionTooltip colors={colors} />} />
          <Bar yAxisId="valor" dataKey="valorInadimplente" name="Valor inadimplente" fill={colors.chart.red} radius={[5, 5, 0, 0]} maxBarSize={34} />
          {showCountLines ? (
            <>
              <Line
                yAxisId="quantidade"
                type="monotone"
                dataKey="titulosInadimplentes"
                name="Títulos inadimplentes"
                stroke={colors.chart.primary}
                strokeWidth={2.4}
                dot={{ r: 3, strokeWidth: 1 }}
                activeDot={{ r: 5 }}
              />
              <Line
                yAxisId="quantidade"
                type="monotone"
                dataKey="clientesInadimplentes"
                name="Clientes inadimplentes"
                stroke={colors.chart.green}
                strokeWidth={2.4}
                dot={{ r: 3, strokeWidth: 1 }}
                activeDot={{ r: 5 }}
              />
              <Legend verticalAlign="bottom" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
            </>
          ) : (
            <Line
              yAxisId="dias"
              type="monotone"
              dataKey="atrasoMedio"
              name="Atraso médio"
              stroke={colors.chart.orange}
              strokeWidth={2.4}
              dot={{ r: 3, strokeWidth: 1 }}
              activeDot={{ r: 5 }}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
}

function CurrencyTooltip({ active, payload, label, colors }) {
  if (!active || !payload?.length) return null;
  const item = payload[0];
  const data = item.payload || {};

  return (
    <Paper elevation={0} sx={{ p: 1.2, borderRadius: 1.5, border: `1px solid ${colors.border}` }}>
      <Typography sx={{ fontSize: 12, fontWeight: 900, color: colors.text }}>{label}</Typography>
      <Typography sx={{ fontSize: 12, color: item.fill || colors.chart.primary }}>
        Valor: {formatCurrency(item.value)}
      </Typography>
      {"quantidadeParcelas" in data ? (
        <Typography sx={{ fontSize: 12, color: colors.muted }}>
          Parcelas: {formatNumber(data.quantidadeParcelas)} | Títulos: {formatNumber(data.quantidadeTitulos)} | Clientes:{" "}
          {formatNumber(data.quantidadeClientes)}
        </Typography>
      ) : null}
    </Paper>
  );
}

function AgingChart({ data, colors }) {
  if (!data.length) return <EmptyChart colors={colors} />;

  return (
    <Box sx={{ height: 320, display: "flex", alignItems: "center" }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 8, left: 4, bottom: 18 }}>
          <CartesianGrid stroke={colors.border} strokeDasharray="3 4" vertical={false} />
          <XAxis dataKey="faixa" tick={{ fill: colors.softMuted, fontSize: 11 }} axisLine={false} tickLine={false} />
          <YAxis
            tickFormatter={formatCompactCurrency}
            tick={{ fill: colors.softMuted, fontSize: 11 }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<CurrencyTooltip colors={colors} />} />
          <Bar dataKey="valorTotal" name="Valor total" fill={colors.chart.orange} radius={[5, 5, 0, 0]} maxBarSize={42} />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}

function CostCentersRanking({ data, colors }) {
  if (!data.length) return <EmptyChart colors={colors} />;

  const maxValue = Number(data[0]?.valorTotalInadimplente || 1);

  return (
    <Stack spacing={1.1}>
      {data.slice(0, 5).map((centro, index) => {
        const percent = Math.max(4, (Number(centro.valorTotalInadimplente || 0) / maxValue) * 100);

        return (
          <Paper
            key={`${centro.companyId}-${centro.costCenterId}-${centro.nome}`}
            elevation={0}
            sx={{ p: 1.35, borderRadius: 1.5, bgcolor: colors.cardAlt, border: `1px solid ${colors.border}` }}
          >
            <Stack direction="row" justifyContent="space-between" spacing={1.2} sx={{ mb: 0.6 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ color: colors.text, fontSize: 13, fontWeight: 900 }} noWrap>
                  {index + 1}. {centro.nome || "Centro de custo sem nome"}
                </Typography>
                <Typography sx={{ color: colors.softMuted, fontSize: 11.5 }}>
                  Empresa {centro.companyId || "-"} | CC {centro.costCenterId || "-"}
                  {centro.cnpj ? ` | ${centro.cnpj}` : ""}
                </Typography>
              </Box>
              <Typography sx={{ color: colors.text, fontSize: 13, fontWeight: 900, flexShrink: 0 }}>
                {formatCurrency(centro.valorTotalInadimplente)}
              </Typography>
            </Stack>
            <Box sx={{ height: 8, borderRadius: 999, bgcolor: colors.card, overflow: "hidden", mb: 0.65 }}>
              <Box sx={{ width: `${percent}%`, height: "100%", bgcolor: colors.chart.primary }} />
            </Box>
            <Typography sx={{ color: colors.muted, fontSize: 11.5 }}>
              {formatNumber(centro.clientesInadimplentes)} clientes | {formatNumber(centro.titulosInadimplentes)} títulos |{" "}
              {formatNumber(centro.parcelasInadimplentes)} parcelas | atraso médio {formatAverageDays(centro.atrasoMedio)}
            </Typography>
          </Paper>
        );
      })}
    </Stack>
  );
}

function TopClientesChart({ data, colors }) {
  if (!data.length) return <EmptyChart colors={colors} />;
  const chartColors = getChartColors(colors);

  return (
    <Stack spacing={1.2}>
      {data.slice(0, 6).map((cliente, index) => {
        const maxValue = Number(data[0]?.valorTotal || 1);
        const percent = Math.max(4, (Number(cliente.valorTotal || 0) / maxValue) * 100);

        return (
          <Box key={`${cliente.clientId}-${cliente.clientName}`}>
            <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 0.6 }}>
              <Typography sx={{ color: colors.text, fontSize: 13, fontWeight: 800, minWidth: 0 }} noWrap>
                {index + 1}. {cliente.clientName || "Cliente sem nome"}
              </Typography>
              <Typography sx={{ color: colors.text, fontSize: 13, fontWeight: 900, flexShrink: 0 }}>
                {formatCurrency(cliente.valorTotal)}
              </Typography>
            </Stack>
            <Box sx={{ height: 8, borderRadius: 999, bgcolor: colors.cardAlt, overflow: "hidden" }}>
              <Box sx={{ width: `${percent}%`, height: "100%", bgcolor: chartColors[index % chartColors.length] }} />
            </Box>
            <Typography sx={{ color: colors.softMuted, fontSize: 11.5, mt: 0.35 }}>
              {formatNumber(cliente.quantidadeTitulos)} títulos | {formatNumber(cliente.quantidadeParcelas)} parcelas | maior atraso{" "}
              {formatDays(cliente.maiorAtraso)}
            </Typography>
          </Box>
        );
      })}
    </Stack>
  );
}

function CompositionChart({ composicaoValor, colors }) {
  const data = [
    { name: "Principal", value: Number(composicaoValor?.principal || 0), color: colors.chart.primary },
    { name: "Juros", value: Number(composicaoValor?.juros || 0), color: colors.chart.orange },
    { name: "Multa", value: Number(composicaoValor?.multa || 0), color: colors.chart.red },
    { name: "Pro rata", value: Number(composicaoValor?.proRata || 0), color: colors.chart.teal },
  ].filter((item) => item.value > 0);

  if (!data.length) return <EmptyChart colors={colors} />;

  return (
    <Box sx={{ height: 280 }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" outerRadius={88} innerRadius={48} paddingAngle={2}>
            {data.map((entry) => (
              <Cell key={entry.name} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip formatter={(value) => formatCurrency(value)} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
        </PieChart>
      </ResponsiveContainer>
    </Box>
  );
}

function SpcSerasaPanel({ spcSerasa, colors }) {
  const data = [
    {
      name: "Enviadas",
      parcelas: Number(spcSerasa?.parcelasEnviadas || 0),
      valor: Number(spcSerasa?.valorEnviado || 0),
      color: colors.green,
    },
    {
      name: "Não enviadas",
      parcelas: Number(spcSerasa?.parcelasNaoEnviadas || 0),
      valor: Number(spcSerasa?.valorNaoEnviado || 0),
      color: colors.orange,
    },
  ];

  return (
    <Stack spacing={1.5}>
      {data.map((item) => (
        <Paper
          key={item.name}
          elevation={0}
          sx={{ p: 1.6, borderRadius: 1.5, bgcolor: colors.cardAlt, border: `1px solid ${colors.border}` }}
        >
          <Stack direction="row" justifyContent="space-between" spacing={1}>
            <Box>
              <Typography sx={{ color: colors.text, fontWeight: 900, fontSize: 14 }}>{item.name}</Typography>
              <Typography sx={{ color: colors.softMuted, fontSize: 12 }}>
                {formatNumber(item.parcelas)} parcelas
              </Typography>
            </Box>
            <Typography sx={{ color: item.color, fontWeight: 900 }}>{formatCurrency(item.valor)}</Typography>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
}

function formatUltimaCargaDateTime(value) {
  if (!value) return "-";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";

  const localDate = new Date(parsed.getTime() - 3 * 60 * 60 * 1000);
  return formatDateTime(localDate).replace(",", " -");
}

function SaudeCarga({ saudeCarga, ultimaCarga, colors }) {
  const items = [
    ["Títulos avaliados", formatNumber(saudeCarga?.totalTitulos)],
    ["Aprovados", formatNumber(saudeCarga?.titulosAprovados)],
    ["Descartados por atraso", formatNumber(saudeCarga?.titulosDescartadosPorAtraso)],
    ["Descartados por valor", formatNumber(saudeCarga?.titulosDescartadosPorValor)],
    ["Separados judicialmente", formatNumber(saudeCarga?.titulosSeparadosJudicial)],
    ["Última carga", formatUltimaCargaDateTime(ultimaCarga?.finishedAt)],
  ];

  return (
    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: colors.card, border: `1px solid ${colors.border}` }}>
      <Typography sx={{ color: colors.text, fontWeight: 900, mb: 1.4 }}>Saúde da carga</Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", md: "repeat(6, minmax(0, 1fr))" }, gap: 1 }}>
        {items.map(([label, value]) => (
          <Box key={label} sx={{ p: 1.2, borderRadius: 1.5, bgcolor: colors.cardAlt }}>
            <Typography sx={{ color: colors.softMuted, fontSize: 11, fontWeight: 800 }}>{label}</Typography>
            <Typography sx={{ color: colors.text, fontSize: 15, fontWeight: 900 }}>{value}</Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
}

export function CobrancaRedirect() {
  return <Navigate to="/cobranca/visao-geral" replace />;
}

export function CobrancaConfiguracao() {
  return (
    <CobrancaShell>
      <ConditionTypesConfigPanel />
    </CobrancaShell>
  );
}

export function CobrancaAvisos() {
  const colors = getCobrancaColors();

  return (
    <CobrancaShell>
      <CobrancaAvisosPanel colors={colors} />
    </CobrancaShell>
  );
}

export function CobrancaWhatsapp() {
  const colors = getCobrancaColors();

  return (
    <CobrancaShell>
      <CobrancaWhatsappTemplatesPanel colors={colors} />
    </CobrancaShell>
  );
}

export function CobrancaInadimplentes() {
  const colors = getCobrancaColors();
  const [regras, setRegras] = useState([]);
  const [diaInput, setDiaInput] = useState("");
  const [centroCustoInput, setCentroCustoInput] = useState("");
  const [filtrosConsultados, setFiltrosConsultados] = useState({ diasAtraso: null, costCenterId: "" });
  const [centrosCusto, setCentrosCusto] = useState([]);
  const [consultaVersao, setConsultaVersao] = useState(0);
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(true);
  const [centrosCustoLoading, setCentrosCustoLoading] = useState(true);
  const [centrosCustoError, setCentrosCustoError] = useState("");
  const [consultando, setConsultando] = useState(false);
  const [error, setError] = useState("");
  const [selectedElegivel, setSelectedElegivel] = useState(null);

  useEffect(() => {
    let active = true;

    const loadRegras = async () => {
      setLoading(true);

      try {
        const data = await cobrancaNotificacoesService.getRegras();
        if (active) {
          setRegras(data);
        }
      } catch (err) {
        if (active) {
          setError(cobrancaNotificacoesService.getApiErrorMessage(err, "Não foi possível carregar as regras configuradas."));
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    loadRegras();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    const loadCentrosCusto = async () => {
      setCentrosCustoLoading(true);
      setCentrosCustoError("");

      try {
        const data = await cobrancaNotificacoesService.getCentrosCusto();
        if (active) setCentrosCusto(data);
      } catch (err) {
        if (active) {
          setCentrosCustoError(cobrancaNotificacoesService.getApiErrorMessage(err, "Não foi possível carregar os centros de custo."));
        }
      } finally {
        if (active) setCentrosCustoLoading(false);
      }
    };

    loadCentrosCusto();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!filtrosConsultados.diasAtraso && !filtrosConsultados.costCenterId) {
      setResultado(null);
      setConsultando(false);
      return undefined;
    }

    let active = true;

    const loadElegiveis = async () => {
      setConsultando(true);
      setError("");

      try {
        const data = await cobrancaNotificacoesService.getElegiveis(filtrosConsultados);
        if (active) setResultado(data);
      } catch (err) {
        if (active) {
          setResultado(null);
          setError(cobrancaNotificacoesService.getApiErrorMessage(err, "Não foi possível carregar os inadimplentes elegíveis."));
        }
      } finally {
        if (active) setConsultando(false);
      }
    };

    loadElegiveis();

    return () => {
      active = false;
    };
  }, [consultaVersao, filtrosConsultados]);

  const handleConsultar = () => {
    const hasDayFilter = Boolean(diaInput);
    const dia = hasDayFilter ? Number(diaInput) : null;

    if (hasDayFilter && (!Number.isInteger(dia) || dia < 1 || dia > 730)) {
      setError("Informe dias de atraso entre 1 e 730.");
      return;
    }

    if (!dia && !centroCustoInput) {
      setError("Informe ao menos um filtro: dias de atraso ou centro de custo.");
      return;
    }

    setFiltrosConsultados({ diasAtraso: dia, costCenterId: centroCustoInput.trim() });
    setConsultaVersao((versao) => versao + 1);
  };

  const handleSelectDia = (dia) => {
    setDiaInput(String(dia));
    setFiltrosConsultados({ diasAtraso: Number(dia), costCenterId: centroCustoInput.trim() });
    setConsultaVersao((versao) => versao + 1);
  };

  const handleLimparFiltros = () => {
    setDiaInput("");
    setCentroCustoInput("");
    setFiltrosConsultados({ diasAtraso: null, costCenterId: "" });
    setConsultaVersao((versao) => versao + 1);
    setError("");
  };

  const metricas = resultado ? [
    { label: "Clientes", value: formatNumber(resultado.totalClientes) },
    { label: "Títulos", value: formatNumber(resultado.totalTitulos) },
    { label: "Parcelas", value: formatNumber(resultado.totalParcelas) },
    { label: "Valor total", value: formatCurrency(resultado.valorTotal) },
  ] : [];

  return (
    <CobrancaShell>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.25, md: 1.5 },
          borderRadius: 2,
          bgcolor: colors.card,
          border: `1px solid ${colors.border}`,
        }}
      >
        <Stack spacing={1.25}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between">
            <Stack direction="row" spacing={1.2} alignItems="center">
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: 2,
                bgcolor: colors.primary,
                color: "#fff",
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}
            >
              <GroupsOutlined sx={{ fontSize: 19 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ color: colors.text, fontWeight: 900 }}>
                Consulta de elegíveis
              </Typography>
              <Typography sx={{ color: colors.muted, fontSize: 13 }}>
                Use os filtros para consultar contatos elegíveis à cobrança.
              </Typography>
            </Box>
            </Stack>
          </Stack>

          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              borderRadius: 1.5,
              bgcolor: colors.cardAlt,
              border: `1px solid ${colors.border}`,
            }}
          >
            <Stack spacing={1.25}>
              <Box>
                <TextField
                  select
                  label="Centro de custo"
                  value={centroCustoInput}
                  onChange={(event) => setCentroCustoInput(event.target.value)}
                  helperText={centrosCustoError || (centrosCustoLoading ? "Carregando centros de custo..." : "Opcional")}
                  size="small"
                  disabled={consultando || centrosCustoLoading || Boolean(centrosCustoError)}
                  fullWidth
                  sx={{ maxWidth: { md: 560 } }}
                >
                  <MenuItem value="">Todos os centros de custo</MenuItem>
                  {centrosCusto.map((centroCusto) => (
                    <MenuItem key={`${centroCusto.companyId}-${centroCusto.costCenterId}`} value={centroCusto.costCenterId}>
                      {centroCusto.nome || "Centro sem nome"} ({centroCusto.costCenterId})
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ xs: "stretch", md: "center" }} justifyContent="space-between">
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                  <TextField
                    label="Dias em atraso"
                    value={diaInput}
                    onChange={(event) => setDiaInput(event.target.value.replace(/\D/g, ""))}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        handleConsultar();
                      }
                    }}
                    helperText="Opcional. Entre 1 e 730"
                    size="small"
                    inputMode="numeric"
                    disabled={loading || consultando}
                    sx={{ width: { xs: "100%", sm: 190 } }}
                  />
                  <Typography sx={{ color: colors.muted, fontSize: 12 }}>Dias configurados:</Typography>
                  {regras.map((regra) => (
                    <Chip
                      key={regra.diasAtraso}
                      label={`${regra.diasAtraso} ${regra.diasAtraso === 1 ? "dia" : "dias"}`}
                      color={Number(filtrosConsultados.diasAtraso) === Number(regra.diasAtraso) ? "primary" : "default"}
                      variant={Number(filtrosConsultados.diasAtraso) === Number(regra.diasAtraso) ? "filled" : "outlined"}
                      onClick={() => handleSelectDia(regra.diasAtraso)}
                      disabled={consultando}
                      size="small"
                      sx={{ fontWeight: 800 }}
                    />
                  ))}
                  {!loading && !regras.length ? <Typography sx={{ color: colors.softMuted, fontSize: 12 }}>Nenhum dia configurado.</Typography> : null}
                </Box>
                <Stack direction="row" spacing={1} justifyContent={{ xs: "flex-start", md: "flex-end" }}>
                  <Button size="small" onClick={handleLimparFiltros} disabled={consultando} sx={{ textTransform: "none", fontWeight: 800 }}>
                    Limpar filtros
                  </Button>
                  <Button variant="contained" size="small" startIcon={consultando ? <CircularProgress color="inherit" size={16} /> : <RefreshRounded />} onClick={handleConsultar} disabled={consultando} sx={{ textTransform: "none", fontWeight: 800 }}>
                    Consultar
                  </Button>
                </Stack>
              </Stack>
            </Stack>
          </Paper>

          {error ? <Alert severity="error" sx={{ borderRadius: 1.5 }}>{error}</Alert> : null}

          {consultando ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ color: colors.muted }}>
              <CircularProgress size={18} />
              <Typography sx={{ fontSize: 13 }}>Carregando inadimplentes elegíveis...</Typography>
            </Stack>
          ) : null}

          {!resultado && !consultando && !error ? (
            <Paper elevation={0} sx={{ p: 2, borderRadius: 1.5, bgcolor: colors.cardAlt, border: `1px dashed ${colors.border}`, textAlign: "center" }}>
              <Typography sx={{ color: colors.muted, fontSize: 13 }}>
                Informe dias de atraso ou um centro de custo para consultar os inadimplentes elegíveis.
              </Typography>
            </Paper>
          ) : null}

          {resultado ? (
            <>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" }, gap: 1.1 }}>
                {metricas.map((metrica) => (
                  <Paper key={metrica.label} elevation={0} sx={{ p: 1.2, minWidth: 0, minHeight: 90, borderRadius: 1.8, bgcolor: "var(--ops-surface)", border: "1px solid var(--ops-border)", borderTop: "2px solid var(--ops-accent)", boxShadow: "0 1px 3px rgba(31, 58, 86, 0.035)" }}>
                    <Typography sx={{ color: colors.softMuted, fontSize: 11 }}>{metrica.label}</Typography>
                    <Typography sx={{ color: colors.text, fontWeight: 900, fontSize: 16, mt: 0.25, overflowWrap: "anywhere" }}>{metrica.value}</Typography>
                  </Paper>
                ))}
              </Box>

              <Paper elevation={0} sx={{ borderRadius: 1.5, border: `1px solid ${colors.border}`, overflow: "hidden" }}>
                <Box sx={{ px: 1.5, py: 1.2, borderBottom: `1px solid ${colors.border}` }}>
                  <Typography sx={{ color: colors.text, fontWeight: 900, fontSize: 14 }}>
                    Contatos elegíveis {resultado.diasAtraso === null ? "em todos os dias de atraso" : `para ${resultado.diasAtraso} ${resultado.diasAtraso === 1 ? "dia" : "dias"} de atraso`}{resultado.costCenterId ? ` · centro de custo ${resultado.costCenterId}` : ""}
                  </Typography>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Cliente</TableCell>
                        <TableCell>Contato</TableCell>
                        <TableCell>Telefone</TableCell>
                        <TableCell align="right">Títulos</TableCell>
                        <TableCell align="right">Parcelas</TableCell>
                        <TableCell align="right">Valor</TableCell>
                        <TableCell align="right" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {resultado.elegiveis.map((elegivel) => (
                        <TableRow key={`${elegivel.erpOrigem}-${elegivel.companyId}-${elegivel.clientId}`} hover>
                          <TableCell>
                            <Typography sx={{ color: colors.text, fontWeight: 800, fontSize: 13 }}>{elegivel.clientName || "--"}</Typography>
                            <Typography sx={{ color: colors.softMuted, fontSize: 11 }}>{elegivel.erpOrigem || "--"}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ color: colors.text, fontSize: 13 }}>{elegivel.contatoSelecionado.nome || "--"}</Typography>
                            <Typography sx={{ color: colors.softMuted, fontSize: 11 }}>{elegivel.contatoSelecionado.tipoContato || "--"}</Typography>
                          </TableCell>
                          <TableCell>{elegivel.contatoSelecionado.telefone || "--"}</TableCell>
                          <TableCell align="right">{formatNumber(elegivel.quantidadeTitulos)}</TableCell>
                          <TableCell align="right">{formatNumber(elegivel.quantidadeParcelas)}</TableCell>
                          <TableCell align="right">{formatCurrency(elegivel.valorTotal)}</TableCell>
                          <TableCell align="right">
                            <IconButton aria-label={`Ver detalhes de ${elegivel.clientName || "cliente"}`} onClick={() => setSelectedElegivel(elegivel)} size="small" sx={{ color: colors.primary }}>
                              <VisibilityOutlined fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {!resultado.elegiveis.length ? (
                  <Box sx={{ px: 1.5, py: 3, textAlign: "center" }}>
                    <Typography sx={{ color: colors.muted, fontSize: 13 }}>Nenhum contato elegível para os filtros selecionados.</Typography>
                  </Box>
                ) : null}
              </Paper>
            </>
          ) : null}
        </Stack>
      </Paper>

      <Dialog open={Boolean(selectedElegivel)} onClose={() => setSelectedElegivel(null)} maxWidth="md" fullWidth>
        <DialogTitle>{selectedElegivel?.clientName || "Detalhes do cliente"}</DialogTitle>
        <DialogContent>
          {selectedElegivel ? (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 1 }}>
                <Typography sx={{ fontSize: 13 }}><strong>Contato:</strong> {selectedElegivel.contatoSelecionado.nome || "--"}</Typography>
                <Typography sx={{ fontSize: 13 }}><strong>Telefone:</strong> {selectedElegivel.contatoSelecionado.telefone || "--"}</Typography>
                <Typography sx={{ fontSize: 13 }}><strong>E-mail:</strong> {selectedElegivel.contatoSelecionado.email || "--"}</Typography>
                <Typography sx={{ fontSize: 13 }}><strong>Documento:</strong> {selectedElegivel.contatoSelecionado.documento || "--"}</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 900, mb: 0.75 }}>Centros de custo</Typography>
                {selectedElegivel.centrosCusto.map((centro) => (
                  <Typography key={`${centro.costCenterId}-${centro.nome}`} sx={{ color: "text.secondary", fontSize: 13 }}>
                    {centro.nome || centro.costCenterId} · {formatCurrency(centro.valorTotal)} · {formatNumber(centro.quantidadeParcelas)} parcelas
                  </Typography>
                ))}
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 900, mb: 0.75 }}>Parcelas</Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead><TableRow><TableCell>Título</TableCell><TableCell>Parcela</TableCell><TableCell>Vencimento</TableCell><TableCell align="right">Valor</TableCell></TableRow></TableHead>
                    <TableBody>
                      {selectedElegivel.parcelas.map((parcela) => (
                        <TableRow key={`${parcela.tituloId}-${parcela.parcelaId}`}>
                          <TableCell>{parcela.numeroTitulo || "--"}</TableCell>
                          <TableCell>{parcela.numeroParcela || "--"}</TableCell>
                          <TableCell>{parcela.dataVencimento || "--"}</TableCell>
                          <TableCell align="right">{formatCurrency(Number(parcela.valor || 0))}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSelectedElegivel(null)} sx={{ textTransform: "none", fontWeight: 800 }}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </CobrancaShell>
  );
}

export function CobrancaTitulos() {
  return (
    <CobrancaShell>
      <CobrancaTitulosPage />
    </CobrancaShell>
  );
}

export function CobrancaCargas() {
  return (
    <CobrancaShell>
      <CobrancaCargasPage />
    </CobrancaShell>
  );
}

export default function CobrancaVisaoGeral() {
  return (
    <CobrancaShell>
      <CobrancaDashboard />
    </CobrancaShell>
  );
}

function CobrancaDashboard() {
  const theme = useTheme();
  const colors = useMemo(
    () => ({ ...getCobrancaColors(), chart: getCobrancaChartPalette(theme) }),
    [theme]
  );
  const [dashboard, setDashboard] = useState(emptyDashboard);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadDashboard = async () => {
    setLoading(true);
    setError("");

    try {
      const data = await cobrancaDashboardService.getDashboard();
      setDashboard(data || emptyDashboard);
    } catch (err) {
      setError(cobrancaDashboardService.getApiErrorMessage(err, "Não foi possível consultar o dashboard de cobrança."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const cards = useMemo(() => dashboard?.cards || {}, [dashboard?.cards]);
  const metrics = useMemo(
    () => [
      {
        label: "Valor inadimplente",
        value: loading ? "..." : formatCurrency(cards.valorTotalInadimplente),
        helper: "Total atualizado em aberto",
        icon: AccountBalanceWalletOutlined,
        variant: "red",
      },
      {
        label: "Clientes",
        value: loading ? "..." : formatNumber(cards.clientesInadimplentes),
        helper: "Clientes inadimplentes",
        icon: GroupsOutlined,
        variant: "blue",
      },
      {
        label: "Títulos",
        value: loading ? "..." : formatNumber(cards.titulosInadimplentes),
        helper: "Títulos inadimplentes",
        icon: ReceiptLongOutlined,
        variant: "purple",
      },
      {
        label: "Parcelas",
        value: loading ? "..." : formatNumber(cards.parcelasInadimplentes),
        helper: "Parcelas vencidas",
        icon: PaidOutlined,
        variant: "teal",
      },
      {
        label: "Atraso médio",
        value: loading ? "..." : formatAverageDays(cards.atrasoMedio),
        helper: "Média de dias em atraso",
        icon: SpeedOutlined,
        variant: "orange",
      },
      {
        label: "Maior atraso",
        value: loading ? "..." : formatDays(cards.maiorAtraso),
        helper: "Maior vencimento em aberto",
        icon: TimelapseRounded,
        variant: "navy",
      },
    ],
    [cards, loading]
  );

  const evolucaoDiaria = useMemo(
    () => normalizeEvolutionData(dashboard?.evolucao?.diaria || [], "diaria"),
    [dashboard?.evolucao?.diaria]
  );

  const evolucaoMensal = useMemo(
    () => normalizeEvolutionData(dashboard?.evolucao?.mensal || [], "mensal"),
    [dashboard?.evolucao?.mensal]
  );

  const faixasAtraso = useMemo(
    () => [...(dashboard?.faixasAtraso || [])].sort((a, b) => Number(a.ordem || 0) - Number(b.ordem || 0)),
    [dashboard?.faixasAtraso]
  );

  const centrosCusto = useMemo(
    () => [...(dashboard?.centrosCusto || [])],
    [dashboard?.centrosCusto]
  );

  return (
    <Stack spacing={1.25}>

        {error ? (
          <Alert severity="error" icon={<ErrorOutlineRounded />} sx={{ borderRadius: 2 }}>
            {error}
          </Alert>
        ) : null}

        {dashboard?.ultimaCarga?.errorMessage ? (
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            {dashboard.ultimaCarga.errorMessage}
          </Alert>
        ) : null}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))", lg: "repeat(6, minmax(0, 1fr))" },
            gap: 1.1,
          }}
        >
          {metrics.map((metric) => (
            <KpiCard key={metric.label} {...metric} />
          ))}
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 1.1 }}>
          <ChartPanel title="Evolução diária" subtitle="Últimos 30 dias com carga OK" colors={colors}>
            {loading ? <EmptyChart colors={colors} /> : <EvolutionChart data={evolucaoDiaria} colors={colors} showCountLines />}
          </ChartPanel>

          <ChartPanel title="Evolução mensal" subtitle="Última foto disponível dos últimos 12 meses com carga OK" colors={colors}>
            {loading ? <EmptyChart colors={colors} /> : <EvolutionChart data={evolucaoMensal} colors={colors} showCountLines />}
          </ChartPanel>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 1.1 }}>
          <ChartPanel title="Inadimplência por centro de custo" subtitle="Ordenado por maior valor inadimplente" colors={colors}>
            {loading ? <EmptyChart colors={colors} /> : <CostCentersRanking data={centrosCusto} colors={colors} />}
          </ChartPanel>

          <ChartPanel title="Inadimplência por faixa de atraso" subtitle="Valor total por faixa de atraso" colors={colors}>
            {loading ? <EmptyChart colors={colors} /> : <AgingChart data={faixasAtraso} colors={colors} />}
          </ChartPanel>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "repeat(3, minmax(0, 1fr))" }, gap: 1.1 }}>
          <ChartPanel title="Top clientes" subtitle="Ordenado por valor inadimplente" colors={colors}>
            {loading ? <EmptyChart colors={colors} /> : <TopClientesChart data={dashboard?.topClientes || []} colors={colors} />}
          </ChartPanel>

          <ChartPanel
            title="Composição do valor"
            subtitle={`Total atualizado: ${formatCurrency(dashboard?.composicaoValor?.totalAtualizado)}`}
            colors={colors}
          >
            {loading ? <EmptyChart colors={colors} /> : <CompositionChart composicaoValor={dashboard?.composicaoValor} colors={colors} />}
          </ChartPanel>

          <ChartPanel title="SPC/Serasa" subtitle="Parcelas enviadas e não enviadas" colors={colors}>
            {loading ? <EmptyChart colors={colors} /> : <SpcSerasaPanel spcSerasa={dashboard?.spcSerasa} colors={colors} />}
          </ChartPanel>
        </Box>

        <SaudeCarga saudeCarga={dashboard?.saudeCarga || {}} ultimaCarga={dashboard?.ultimaCarga} colors={colors} />
    </Stack>
  );
}
